import { useState, useMemo } from 'react';
import { CaretUp, CaretDown, CaretLeft, CaretRight } from '@phosphor-icons/react';

export default function SortableTable({ columns, data, pageSize = 15, emptyMessage = 'No data found', emptyIcon: EmptyIcon }) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(0);

  const sorted = useMemo(() => {
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      let va = a[sortKey], vb = b[sortKey];
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      va = String(va).toLowerCase(); vb = String(vb).toLowerCase();
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });
  }, [data, sortKey, sortDir]);

  const totalPages = Math.ceil(sorted.length / pageSize);
  const pageData = sorted.slice(page * pageSize, (page + 1) * pageSize);

  const toggleSort = (key) => {
    if (sortKey === key) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortKey(key); setSortDir('asc'); }
    setPage(0);
  };

  return (
    <div className="bg-white border border-[#e5e5ea] rounded-xl overflow-hidden" data-testid="sortable-table">
      {data.length === 0 ? (
        <div className="p-12 text-center">
          {EmptyIcon && <EmptyIcon size={44} className="text-[#d1d1d6] mx-auto mb-3" />}
          <p className="text-[13px] text-[#9c9db7]">{emptyMessage}</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#e5e5ea]">
                  {columns.map((col, ci) => (
                    <th key={ci} className="text-left p-3 first:pl-4 last:pr-4">
                      {col.sortable !== false ? (
                        <button onClick={() => toggleSort(col.key)} className="sortable-th text-[11px] font-semibold uppercase tracking-[0.04em] text-[#9c9db7]" title={`Sort by ${col.label}`}>
                          {col.label}
                          <span className="flex flex-col -space-y-1">
                            <CaretUp size={10} className={sortKey === col.key && sortDir === 'asc' ? 'text-[#7d64ff]' : 'text-[#d1d1d6]'} weight="bold" />
                            <CaretDown size={10} className={sortKey === col.key && sortDir === 'desc' ? 'text-[#7d64ff]' : 'text-[#d1d1d6]'} weight="bold" />
                          </span>
                        </button>
                      ) : (
                        <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#9c9db7]">{col.label}</span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageData.map((row, ri) => (
                  <tr key={ri} className="border-b border-[#f0f0f0] hover:bg-[#fafafe] transition-colors">
                    {columns.map((col, ci) => (
                      <td key={ci} className="p-3 first:pl-4 last:pr-4 text-[13px]">
                        {col.render ? col.render(row[col.key], row) : row[col.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#f0f0f0]" data-testid="table-pagination">
              <span className="text-[12px] text-[#9c9db7]">
                Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, sorted.length)} of {sorted.length}
              </span>
              <div className="pagination">
                <button className="pagination-btn" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="page-prev">
                  <CaretLeft size={14} />
                </button>
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  let p;
                  if (totalPages <= 7) p = i;
                  else if (page < 3) p = i;
                  else if (page > totalPages - 4) p = totalPages - 7 + i;
                  else p = page - 3 + i;
                  return (
                    <button key={p} className={`pagination-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)} data-testid={`page-${p}`}>
                      {p + 1}
                    </button>
                  );
                })}
                <button className="pagination-btn" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="page-next">
                  <CaretRight size={14} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
